import { Assets, Container, Graphics, Sprite, Text, type Texture, type Ticker } from 'pixi.js';
import type {
  PlayOffer,
  RealmFound,
  RealmPlace,
  RealmPlayed,
  RealmOpened,
  RealmClaimed,
  RealmView,
  RealmWished,
} from '@goodtime/shared';
import { AudioBus } from '../../audio/AudioBus';
import { AssetLoader } from '../../asset/AssetLoader';
import { TakeWinAnimation } from '../../effects/TakeWinAnimation';
import {
  BoardDialog,
  BrassButton,
  COLOURS,
  Confetti,
  TitleCard,
  animate,
  displayStyle,
  ease,
  layoutButtonRow,
  prefersReducedMotion,
  uiStyle,
} from '../board';
import { MiniGameLayer } from '../minigames/MiniGameLayer';
import type { MiniGameSnapshot, PlayApi } from '../minigames/types';
import {
  REALM_SPOTS,
  SECRET_SPOTS,
  RealmRefused,
  coinsText,
  refusalLine,
  rulesLine,
  spotState,
  spotWorld,
  statusLine,
  type RealmSpot,
  type SpotId,
} from '../../logic/realm/spots';
import { RealmGlint, RealmPin, RealmTitlePlaque, RoundPicture } from './RealmSpotPieces';
import { BezelButton, drawCrown } from './RealmPieces';

/** The server calls the map's places make, supplied by the app. Each
 *  rejects with {@link RealmRefused} when the server turns it down. */
export interface RealmPlacesApi {
  /** The player's realm, or null when the map is off for them. */
  load(): Promise<RealmView | null>;
  open(placeId: string): Promise<RealmOpened>;
  play(placeId: string): Promise<RealmPlayed>;
  claim(): Promise<RealmClaimed>;
  wish(): Promise<RealmWished>;
  find(secretId: string): Promise<RealmFound>;
  /** The calls a game's screen makes for one play. */
  playApi(playId: string): PlayApi;
  /** Fetch a mini-game's art and sounds before it opens. */
  loadGameArt(kind: string): Promise<void>;
  /** The player's coins as the app shows them. */
  coins(): number;
  /** The server's wallet moved: the app fetches and shows it (after a spend
   *  at once; after a prize once its coins have landed). */
  walletChanged(): void;
}

/** Art for the places, by URL. */
export interface RealmPlacesArt {
  /** Each place's painted picture, by spot id. */
  pictures: Partial<Record<SpotId, string>>;
  sparkle: string;
  coin: string;
  clock: string;
  /** The multiplayer Wheel's crown medallion. */
  crown: string;
  gift: string;
}

export interface RealmPlacesOptions {
  api: RealmPlacesApi;
  art: RealmPlacesArt;
  /** Show the map's title plaque (its first view only). */
  showTitle?: boolean;
  onTitleSeen?: () => void;
  /** Where the coin counter is, in canvas pixels (coins fly there). */
  coinTarget?: () => { x: number; y: number } | null;
  /** The multiplayer Wheel, for the world in view (the old world map's
   *  crown). */
  onWheel?: (world: number) => void;
  /** The daily gift (shown while there's one to claim). */
  onGift?: () => void;
  giftReady?: boolean;
  /** Where pins and glints sit when the map is not the realm painting: by
   *  place id (`fountain`) or secret id (`s1`), as fractions of the map.
   *  An id left out keeps its place on the realm painting. */
  positions?: Readonly<Record<string, { x: number; y: number }>>;
  /** The realm view arrived from the server (every fetch that returned
   *  one, the first and each {@link RealmPlaces.reload}). */
  onRealm?: (view: RealmView) => void;
}

/** What the places need from the map. */
export interface RealmPlacesHost {
  readonly stage: Container;
  readonly ticker: Ticker;
  /** A world's floor (index into BIOMES) if it has loaded; asking starts
   *  its load, and {@link RealmPlaces.floorArrived} brings it. */
  floor(world: number): Texture | null;
  bezel(): Texture | null;
  lock(): Texture | null;
  /** True when the press that's ending was a drag, not a tap. */
  dragged(): boolean;
  /** The world in view (index into BIOMES). */
  world(): number;
  /** Something now covers the map, or no longer does. */
  coverChanged(): void;
  /** A pin changed size: place the map's names again. */
  relayout(): void;
}

/** Sounds by moment (aliases the app loads: assets/audio/realm). */
export const REALM_SOUNDS = {
  pageTurn: 'realm:page_turn',
  unlock: 'realm:place_unlock',
  sparkle: 'realm:secret_sparkle',
  coins: 'road:coin_burst',
} as const;

/** The open place card. */
interface PlaceCard {
  id: SpotId;
  /** The world the place is in: its board sits on that world's floor. */
  world: number;
  board: BoardDialog;
  ui: PlaceCardUi;
  buttons: BrassButton[];
  picture?: RoundPicture;
  resultText?: Text;
  /** An action (opening, a wish, a gift) is running: its buttons wait. */
  busy: boolean;
}

/** A place card's state as plain data (browser tests). */
export interface PlaceCardUi {
  id: string;
  title: string;
  lines: string[];
  result: string;
  buttons: string[];
  /** An action is running; the buttons wait for it. */
  busy: boolean;
}

/**
 * The realm map's side places (milestone 2): their pins, the place cards
 * (on the board kit), the daily games (the road's mini-games, each after its
 * TV title card), the warm pool's gift, the fountain's wishes, the secrets'
 * glints, the title plaque on the first view, and the HUD's Wheel and gift.
 * Coins won fly into the coin counter. The map owns the camera and the
 * layers; this owns everything on them that's about places.
 */
export class RealmPlaces {
  /** Pins, in the world (the map places and scales them as names). */
  readonly pins: RealmPin[] = [];
  /** Glints, in the world, under the names. */
  readonly glintLayer = new Container();
  /** Over everything: the title plaque, a card, a game, the coins. */
  readonly overlay = new Container();
  /** HUD controls for the map's HUD: the Wheel, and the gift. */
  readonly crown: Container;
  readonly gift: BezelButton;

  private view: RealmView | null = null;
  private readonly glints = new Map<string, RealmGlint>();
  private readonly textures = new Map<string, Texture | null>();
  private card: PlaceCard | null = null;
  private readonly cardMoves = new WeakMap<BoardDialog, Promise<void>>();
  private readonly miniGames: MiniGameLayer;
  private readonly titleCard = new TitleCard();
  private readonly coinFlight: TakeWinAnimation;
  private readonly burst = new Confetti();
  private plaque: RealmTitlePlaque | null = null;
  /** A secret is being found. */
  private finding = false;
  private gaming = false;
  /** The world of the place whose game is playing: its board's floor. */
  private gameWorld = 0;
  private zoom = 1;
  private glintK = 1;
  private viewW = 1;
  private viewH = 1;
  private refreshTimer: ReturnType<typeof setInterval> | null = null;
  private destroyed = false;
  private readonly onTick = (t: Ticker): void => this.tick(t.deltaMS);

  constructor(
    private readonly host: RealmPlacesHost,
    private readonly opts: RealmPlacesOptions,
    map: { w: number; h: number },
  ) {
    for (const s of REALM_SPOTS) {
      // Its world's floor comes through floorArrived, once wanted.
      const pin = new RealmPin(s, { bezel: host.bezel(), lock: host.lock(), coin: null, clock: null });
      const at = opts.positions?.[s.id] ?? s;
      pin.position.set(at.x * map.w, at.y * map.h);
      pin.on('pointertap', () => this.tapPin(s));
      this.pins.push(pin);
    }
    for (const g of SECRET_SPOTS) {
      const glint = new RealmGlint(g.id);
      const at = opts.positions?.[g.id] ?? g;
      glint.position.set(at.x * map.w, at.y * map.h);
      glint.visible = false;
      glint.on('pointertap', () => void this.findSecret(g.id));
      this.glints.set(g.id, glint);
      this.glintLayer.addChild(glint);
    }
    this.crown = new Container();
    this.crown.label = 'wheel';
    this.crown.eventMode = 'static';
    this.crown.cursor = 'pointer';
    this.crown.on('pointertap', () => this.opts.onWheel?.(this.host.world()));
    this.gift = new BezelButton(host.bezel(), null, 'Gift', () => this.opts.onGift?.());
    this.gift.label = 'gift';
    this.gift.visible = !!opts.giftReady;

    this.miniGames = new MiniGameLayer({
      texture: (alias) => AssetLoader.shared.getTexture(alias),
      floor: () => this.host.floor(this.gameWorld),
      picture: () => null,
      flyCoins: (from, amount) => this.flyCoins(from, amount),
      titleCard: (text, o) => this.titleCard.play(text, o),
      loadArt: (kind) => this.opts.api.loadGameArt(kind),
    });
    this.coinFlight = new TakeWinAnimation(host.ticker);
    this.overlay.addChild(this.miniGames, this.titleCard, this.burst, this.coinFlight);
    host.ticker.add(this.onTick);
  }

  /** Load the art, then the player's realm. */
  async init(): Promise<void> {
    const art = this.opts.art;
    const urls: [string, string][] = [
      ...Object.entries(art.pictures).map(([k, v]) => [`pic:${k}`, v as string] as [string, string]),
      ['sparkle', art.sparkle],
      ['coin', art.coin],
      ['clock', art.clock],
      ['crown', art.crown],
      ['gift', art.gift],
    ];
    await Promise.all(
      urls.map(async ([k, url]) => this.textures.set(k, await Assets.load<Texture>(url).catch(() => null))),
    );
    if (this.destroyed) return;
    for (const pin of this.pins) {
      pin.setPicture(this.tex(`pic:${pin.spot.id}`));
      pin.setIcons(this.tex('coin'), this.tex('clock'));
    }
    for (const g of this.glints.values()) g.setSparkle(this.tex('sparkle'));
    this.buildCrown();
    const giftPic = this.tex('gift');
    if (giftPic) {
      const s = new Sprite(giftPic);
      s.anchor.set(0.5);
      s.scale.set(26 / Math.max(giftPic.width, giftPic.height));
      this.gift.addChildAt(s, 1);
    }
    await this.reload();
    this.refreshTimer = setInterval(() => this.refreshPins(), 20_000);
  }

  private tex(key: string): Texture | null {
    return this.textures.get(key) ?? null;
  }

  private buildCrown(): void {
    drawCrown(this.crown, this.tex('crown'));
  }

  /** A world's floor has arrived: its places' plates and an open card of
   *  one of them go onto it. */
  floorArrived(world: number, texture: Texture): void {
    for (const pin of this.pins) if (spotWorld(pin.spot) === world) pin.setFloor(texture);
    if (this.card?.world === world) this.card.board.setFloor(texture);
  }

  /** Fetch the realm again and show it. */
  async reload(): Promise<void> {
    let loaded = false;
    try {
      const view = await this.opts.api.load();
      if (this.destroyed) return;
      this.view = view;
      loaded = true;
    } catch (err) {
      console.warn('[RealmPlaces] realm not loaded:', err);
    }
    this.refreshPins();
    const found = new Set(this.view?.secrets.found ?? []);
    for (const [id, g] of this.glints) g.visible = !!this.view && !found.has(id);
    // Outside the load's try: a throwing callback is the caller's own fault,
    // not a failed load, and the pins above have already refreshed.
    try {
      if (loaded && this.view) this.opts.onRealm?.(this.view);
    } catch (err) {
      console.warn('[RealmPlaces] onRealm failed:', err);
    }
  }

  private place(id: string): RealmPlace | null {
    return this.view?.places.find((p) => p.id === id) ?? null;
  }

  /** Swap in a place as the server sent it after an action. */
  private update(place: RealmPlace): void {
    if (!this.view) return;
    this.view = { ...this.view, places: this.view.places.map((p) => (p.id === place.id ? place : p)) };
    this.refreshPins();
  }

  private refreshPins(): void {
    const now = Date.now();
    for (const pin of this.pins) pin.set(this.place(pin.spot.id), now);
    this.host.relayout();
  }

  // ---- the map's calls ----------------------------------------------------

  /** The camera's zoom changed: glints stay screen size. */
  onCamera(zoom: number): void {
    this.zoom = zoom;
    for (const g of this.glints.values()) g.scale.set(this.glintK / zoom);
  }

  layout(w: number, h: number): void {
    this.viewW = w;
    this.viewH = h;
    this.card?.board.layout(w, h);
    this.miniGames.layout(w, h);
    this.titleCard.layout(w, h);
    if (this.plaque) this.placePlaque(this.plaque);
  }

  /** The plaque sits a little above the middle, narrow enough on a phone
   *  to stay clear of the buttons down each side. */
  private placePlaque(plaque: RealmTitlePlaque): void {
    plaque.scale.set(1);
    plaque.scale.set(Math.min(1, (this.viewW - 144) / Math.max(1, plaque.width)));
    plaque.position.set(this.viewW / 2, Math.round(this.viewH * 0.4));
  }

  /** Something of the places' covers the map: a card, a game, a title card. */
  get covering(): boolean {
    return !!this.card || this.gaming || this.titleCard.visible;
  }

  setGiftReady(ready: boolean): void {
    this.gift.visible = ready;
  }

  /** Escape: close the card. */
  escape(): boolean {
    if (!this.card) return false;
    this.closeCard();
    return true;
  }

  /**
   * Show the title plaque, once (the map's first view). While something
   * covers the map (the daily bonus at launch, a dialog), it waits, so a
   * new player sees it rather than having it time out underneath.
   */
  showTitle(): void {
    if (!this.opts.showTitle || this.plaque || this.titleDone) return;
    if (this.covered || this.card || this.gaming) {
      this.titleWanted = true;
      return;
    }
    this.titleWanted = false;
    this.titleDone = true;
    const plaque = new RealmTitlePlaque('The Goodtime Realms');
    this.plaque = plaque;
    this.placePlaque(plaque);
    plaque.alpha = 0;
    this.overlay.addChildAt(plaque, 0);
    this.opts.onTitleSeen?.();
    const y = plaque.y;
    void animate(500, (t) => {
      plaque.alpha = Math.min(1, t * 1.6);
      plaque.y = y - 30 * (1 - ease.outBack(t));
    }).then(() => this.timeTitle());
  }

  /** The plaque goes five seconds after it's up and uncovered. */
  private timeTitle(): void {
    if (this.titleTimer) clearTimeout(this.titleTimer);
    this.titleTimer = null;
    if (!this.plaque || this.covered) return;
    this.titleTimer = setTimeout(() => void this.hideTitle(), 5000);
  }

  private titleTimer: ReturnType<typeof setTimeout> | null = null;
  private titleWanted = false;
  private titleDone = false;
  private covered = false;

  /** The app covers the map (a dialog over it), or uncovers it: the
   *  title waits for it. */
  setCovered(covered: boolean): void {
    if (covered === this.covered) return;
    this.covered = covered;
    if (covered) {
      if (this.titleTimer) clearTimeout(this.titleTimer);
      this.titleTimer = null;
    } else if (this.titleWanted) {
      this.showTitle();
    } else {
      this.timeTitle();
    }
  }

  /** The plate is up (browser tests). */
  get titleShown(): boolean {
    return !!this.plaque && this.plaque.visible;
  }

  async hideTitle(): Promise<void> {
    const plaque = this.plaque;
    if (!plaque || plaque.destroyed) return;
    if (this.titleTimer) clearTimeout(this.titleTimer);
    this.titleTimer = null;
    plaque.eventMode = 'none';
    await animate(300, (t) => (plaque.alpha = 1 - t));
    if (this.plaque === plaque) this.plaque = null;
    plaque.destroy({ children: true });
  }

  /** Where a pin is on screen, for tests: the middle of its picture. */
  spotPoint(id: string): { x: number; y: number } | null {
    const pin = this.pins.find((p) => p.spot.id === id);
    if (!pin) return null;
    // The ring's middle: on the spot's line, and always inside the pin's tap area (the plate may have been moved aside).
    const c = pin.ringCentre();
    const at = pin.toGlobal(c);
    return { x: at.x, y: at.y };
  }

  /** A map with its own art over the glints (the strip) hides one while something is over it, and brings it back (a fade, or at once
   *  under reduced motion). */
  blockGlint(id: string, blocked: boolean, immediate = prefersReducedMotion()): void {
    this.glints.get(id)?.setBlocked(blocked, immediate);
  }

  /** A glint is hidden now (the strip's placing), or is not there. */
  glintBlocked(id: string): boolean {
    return this.glints.get(id)?.isBlocked ?? true;
  }

  /** The glints' fades, `ms` on (the strip calls it every frame). */
  stepGlints(ms: number): void {
    for (const g of this.glints.values()) g.fade(ms);
  }

  /** How big the glints are, as a share of screen size: 1 unless the map is so small that they would crowd it (the strip on a
   *  landscape phone). */
  setGlintScale(k: number): void {
    this.glintK = k;
    this.onCamera(this.zoom);
  }

  glintPoint(id: string): { x: number; y: number } | null {
    const g = this.glints.get(id);
    if (!g || !g.visible || g.destroyed || g.isBlocked) return null;
    const p = g.getGlobalPosition();
    return { x: p.x, y: p.y };
  }

  /** The realm as loaded (tests). */
  get realm(): RealmView | null {
    return this.view;
  }

  get cardUi(): PlaceCardUi | null {
    return this.card
      ? { ...this.card.ui, busy: this.card.busy, buttons: this.card.buttons.filter((b) => b.visible).map((b) => b.labelText) }
      : null;
  }

  pressCard(label: string): boolean {
    const b = this.card?.buttons.find((x) => x.visible && x.labelText.startsWith(label));
    if (!b) return false;
    b.emit('pointertap', undefined as never);
    return true;
  }

  get miniGameUi(): MiniGameSnapshot | null {
    return this.miniGames.snapshot;
  }

  pressMiniGame(label: string): boolean {
    return this.miniGames.press(label);
  }

  tapMiniGame(index: number): boolean {
    return this.miniGames.tap(index);
  }

  /** A card's button position on screen (tests tap it like a player). */
  cardButtonPoint(label: string): { x: number; y: number } | null {
    const b = this.card?.buttons.find((x) => x.visible && x.labelText.startsWith(label));
    if (!b) return null;
    const r = b.getBounds();
    return { x: r.x + r.width / 2, y: r.y + r.height / 2 };
  }

  destroy(): void {
    this.destroyed = true;
    if (this.refreshTimer) clearInterval(this.refreshTimer);
    if (this.titleTimer) clearTimeout(this.titleTimer);
    this.host.ticker.remove(this.onTick);
    this.coinFlight.destroyEffect();
  }

  // ---- motion -------------------------------------------------------------

  private tick(dt: number): void {
    if (prefersReducedMotion()) return;
    for (const pin of this.pins) pin.tick(dt);
    for (const g of this.glints.values()) if (g.visible) g.tick(dt);
  }

  // ---- the place cards ----------------------------------------------------

  private tapPin(s: RealmSpot): void {
    if (this.host.dragged() || this.card?.busy || this.gaming) return;
    void this.hideTitle();
    this.openCard(s.id);
  }

  private openCard(id: SpotId): void {
    if (this.card) this.retireCard(this.card.board);
    const spot = REALM_SPOTS.find((s) => s.id === id)!;
    const world = spotWorld(spot);
    const board = new BoardDialog({ title: spot.name, floor: this.host.floor(world), maxWidth: 360 });
    board.onDimTap = () => {
      if (!this.card?.busy) this.closeCard();
    };
    const ui: PlaceCardUi = { id, title: spot.name, lines: [], result: '', buttons: [], busy: false };
    this.card = { id, world, board, ui, buttons: [], busy: false };
    this.overlay.addChildAt(board, this.plaque ? 1 : 0);
    board.layout(this.viewW, this.viewH);
    this.drawCard();
    AudioBus.shared.play(REALM_SOUNDS.pageTurn, { volume: 0.8 });
    this.cardMoves.set(board, board.open());
    this.host.coverChanged();
  }

  /** (Re)draw the open card for its place as it stands. `result` is a line
   *  in big type (a prize). */
  private drawCard(note?: string, result?: { text: string; good: boolean }): void {
    const card = this.card;
    if (!card) return;
    const { board, id } = card;
    const spot = REALM_SPOTS.find((s) => s.id === id)!;
    const place = this.place(id);
    const now = Date.now();
    for (const c of board.content.removeChildren()) c.destroy({ children: true });
    const width = board.contentWidth;
    let y = 0;

    const pic = new RoundPicture(54);
    pic.setTexture(this.tex(`pic:${id}`));
    pic.setGreyed(!place || spotState(place, now) === 'locked');
    pic.position.set(width / 2, y + 58);
    const ring = new Graphics().circle(0, 0, 56).stroke({ color: 0xd9a441, width: 4 }).circle(0, 0, 60).stroke({ color: 0x3f230b, width: 2 });
    ring.position.copyFrom(pic.position);
    board.content.addChild(pic, ring);
    card.picture = pic;
    y += 124;

    const lines: string[] = [];
    const say = (text: string, style: ReturnType<typeof uiStyle>, gap = 6) => {
      const t = new Text({ text, style: { ...style, align: 'center', wordWrap: true, wordWrapWidth: width } });
      t.anchor.set(0.5, 0);
      t.position.set(width / 2, y);
      board.content.addChild(t);
      y += t.height + gap;
      lines.push(text);
      return t;
    };

    if (result) {
      const r = new Text({ text: result.text, style: displayStyle(34, result.good ? COLOURS.gold : COLOURS.loss) });
      r.anchor.set(0.5, 0);
      r.position.set(width / 2, y);
      board.content.addChild(r);
      card.resultText = r;
      y += r.height + 4;
    }
    card.ui.result = result?.text ?? '';
    say(spot.blurb, uiStyle(16, COLOURS.cream, '600'));
    if (place) say(rulesLine(place), { ...uiStyle(13, 0xf0dcb4, '500') });
    // What just happened (a place opened, a wish came true, a refusal),
    // then where the place stands now.
    if (note) say(note, uiStyle(16, COLOURS.gold, '700'));
    const short = !!place && spotState(place, now) === 'locked' && this.opts.api.coins() < place.cost;
    const status = !place
      ? 'Just a moment...'
      : short
        ? refusalLine({ error: 'NOT_ENOUGH_COINS', cost: place.cost, coins: this.opts.api.coins() }, now)
        : statusLine(place, now, this.view?.dayEndsAt);
    say(status, uiStyle(15, note ? COLOURS.cream : COLOURS.gold, '700'), 12);
    card.ui.lines = lines;

    const { cancel, action } = this.cardButtons(id, place, now);
    card.buttons = [cancel, action].filter((b): b is BrassButton => !!b);
    layoutButtonRow(cancel, action, 0, y, width);
    for (const b of card.buttons) board.content.addChild(b);
    if (short) action.setEnabled(false);
    // While an action runs, its card waits: nothing to tap twice.
    if (card.busy) for (const b of card.buttons) b.setEnabled(false);
    board.setContentHeight(y + action.height);
    board.layout(this.viewW, this.viewH);
  }

  private cardButtons(id: SpotId, place: RealmPlace | null, now: number): { cancel: BrassButton | null; action: BrassButton } {
    const button = (label: string, kind: 'gold' | 'wood', onTap: () => void) =>
      new BrassButton({ label, kind, width: 150, height: 46, size: 17, onTap });
    const close = (label: string) => button(label, 'wood', () => this.closeCard());
    if (!place) return { cancel: null, action: close('Close') };
    switch (spotState(place, now)) {
      case 'locked':
        return {
          cancel: close('Not now'),
          action: button(`OPEN FOR ${coinsText(place.cost)}`, 'gold', () => void this.run(() => this.openPlace(id))),
        };
      case 'ready':
        if (place.kind === 'wish') {
          return { cancel: close('Close'), action: button(`WISH FOR ${coinsText(place.price ?? 100)}`, 'gold', () => void this.run(() => this.makeWish())) };
        }
        if (place.kind === 'rest') {
          return { cancel: close('Close'), action: button(`COLLECT ${coinsText(place.gift ?? 50)}`, 'gold', () => void this.run(() => this.collect())) };
        }
        return { cancel: close('Not now'), action: button('PLAY', 'gold', () => void this.run(() => this.playGame(id))) };
      default:
        return { cancel: null, action: close('OK') };
    }
  }

  /** One action at a time: buttons ignore taps while one runs. When it
   *  ends, the card shows the place as it now stands, with the action's
   *  note and result. */
  private async run(action: () => Promise<void>): Promise<void> {
    const card = this.card;
    if (!card || card.busy) return;
    card.busy = true;
    this.lastNote = null;
    this.lastResult = null;
    for (const b of card.buttons) b.setEnabled(false);
    try {
      await action();
    } catch (err) {
      if (err instanceof RealmRefused) {
        await this.reload();
        this.lastNote = refusalLine(err.refusal, Date.now());
      } else {
        console.warn('[RealmPlaces] action failed:', err);
        this.lastNote = "That didn't work. Try again in a moment.";
      }
    } finally {
      card.busy = false;
      // Only the card it ran on: another may be open by now.
      if (this.card === card) this.drawCard(this.lastNote ?? undefined, this.lastResult ?? undefined);
    }
  }

  private lastNote: string | null = null;
  private lastResult: { text: string; good: boolean } | null = null;

  private closeCard(): void {
    const card = this.card;
    if (!card) return;
    this.card = null;
    card.board.eventMode = 'none';
    this.cardMoves.set(card.board, card.board.close());
    this.retireCard(card.board);
    this.host.coverChanged();
  }

  private retireCard(board: BoardDialog): void {
    board.eventMode = 'none';
    void (this.cardMoves.get(board) ?? Promise.resolve()).then(() => {
      board.removeFromParent();
      board.destroy({ children: true });
    });
  }

  // ---- actions --------------------------------------------------------------

  private async openPlace(id: SpotId): Promise<void> {
    const res = await this.opts.api.open(id);
    this.update(res.place);
    this.opts.api.walletChanged();
    AudioBus.shared.play(REALM_SOUNDS.unlock, { volume: 0.9 });
    this.lastNote = `${REALM_SPOTS.find((s) => s.id === id)!.name} is open!`;
    this.drawCard(this.lastNote);
    void this.card?.board.celebrate();
  }

  private async makeWish(): Promise<void> {
    const card = this.card;
    const res = await this.opts.api.wish();
    this.update(res.place);
    // The coin goes in, the water splashes, then the wish comes true.
    if (card?.picture) await this.tossCoin(card);
    const good = res.prize >= res.cost || res.gems > 0;
    const text = `+${coinsText(res.prize)}${res.gems ? ` and ${res.gems} gem` : ''}`;
    this.lastResult = { text, good };
    this.lastNote = res.gems ? 'Something sparkles in the water!' : good ? 'Your wish comes true!' : 'A little wish comes true.';
    this.drawCard(this.lastNote, this.lastResult);
    if (good) void this.card?.board.celebrate();
    const from = this.card?.resultText?.getGlobalPosition() ?? { x: this.viewW / 2, y: this.viewH / 2 };
    await this.flyCoins({ x: from.x, y: from.y + 20 }, res.prize);
    this.opts.api.walletChanged();
  }

  private async collect(): Promise<void> {
    const res = await this.opts.api.claim();
    this.update(res.place);
    this.lastResult = { text: `+${coinsText(res.gift)}`, good: true };
    this.lastNote = 'Ahh, lovely. A gift from the warm pool.';
    this.drawCard(this.lastNote, this.lastResult);
    void this.card?.board.celebrate();
    const from = this.card?.resultText?.getGlobalPosition() ?? { x: this.viewW / 2, y: this.viewH / 2 };
    await this.flyCoins({ x: from.x, y: from.y + 20 }, res.gift);
    this.opts.api.walletChanged();
  }

  /** Today's game: the card goes, the title card plays, then the game. */
  private async playGame(id: SpotId): Promise<void> {
    const res = await this.opts.api.play(id);
    this.update(res.place);
    this.gameWorld = spotWorld(REALM_SPOTS.find((s) => s.id === id)!);
    this.closeCard();
    await this.playOffer(res.offer);
    await this.reload();
    this.opts.api.walletChanged();
  }

  private async playOffer(offer: PlayOffer): Promise<void> {
    if (!this.miniGames.plays(offer.kind)) return;
    this.gaming = true;
    this.host.coverChanged();
    // Over anything added since: the game, then its title card and coins.
    this.host.stage.addChild(this.overlay);
    try {
      await this.miniGames.play(offer, this.opts.api.playApi(offer.playId));
    } finally {
      this.gaming = false;
      this.host.coverChanged();
    }
  }

  private async findSecret(id: string): Promise<void> {
    const glint = this.glints.get(id);
    if (!glint || !glint.visible || this.host.dragged() || this.finding || this.covering) return;
    this.finding = true;
    void this.hideTitle();
    try {
      const res = await this.opts.api.find(id);
      const at = glint.getGlobalPosition();
      AudioBus.shared.play(REALM_SOUNDS.sparkle, { volume: 0.9 });
      if (this.view) this.view = { ...this.view, secrets: res.secrets };
      glint.spend();
      void animate(420, (t) => {
        glint.alpha = 1 - t;
        glint.scale.set((this.glintK * (1 + 1.5 * ease.outCubic(t))) / this.zoom);
      }).then(() => (glint.visible = false));
      if (!res.already) {
        const coins = res.coins ?? 0;
        const gems = res.gems ?? 0;
        this.say(at, coins ? `+${coinsText(coins)} coins` : `+${gems} gem${gems === 1 ? '' : 's'}`);
        void this.burst.burst(at.x, at.y, { count: 40, spread: 200 });
        if (coins) await this.flyCoins(at, coins);
        else if (gems) await this.flyCoins(at, gems, 'shared:icon:gem');
        if (res.secrets.found.length === res.secrets.total) this.say({ x: at.x, y: at.y + 40 }, 'Every secret in the realm found!');
      }
      this.opts.api.walletChanged();
    } catch (err) {
      console.warn('[RealmPlaces] secret not found:', err);
    } finally {
      this.finding = false;
    }
  }

  // ---- little pieces ----------------------------------------------------------

  /** The coin goes into the fountain: it arcs from the button into the
   *  picture and the water rings. */
  private async tossCoin(card: NonNullable<RealmPlaces['card']>): Promise<void> {
    const pic = card.picture;
    if (!pic) return;
    const to = pic.getGlobalPosition();
    const action = card.buttons[card.buttons.length - 1];
    const fromB = action?.getBounds();
    const from = fromB ? { x: fromB.x + fromB.width / 2, y: fromB.y } : { x: to.x, y: to.y + 120 };
    const coinTex = this.tex('coin');
    let coin: Container;
    let k0 = 1;
    if (coinTex) {
      const sprite = new Sprite(coinTex);
      sprite.anchor.set(0.5);
      k0 = 30 / Math.max(coinTex.width, coinTex.height);
      coin = sprite;
    } else {
      coin = new Graphics().circle(0, 0, 15).fill(0xffd36b).stroke({ color: 0xc8963a, width: 2 });
    }
    this.overlay.addChild(coin);
    AudioBus.shared.play('road:coin_single_2', { volume: 0.8 });
    await animate(520, (t) => {
      coin.position.set(from.x + (to.x - from.x) * t, from.y + (to.y - from.y) * t - Math.sin(t * Math.PI) * 90);
      coin.scale.set(k0 * (1 - 0.5 * t));
    });
    coin.destroy();
    const ripple = new Graphics();
    ripple.position.copyFrom(to);
    this.overlay.addChild(ripple);
    await animate(480, (t) => {
      ripple.clear().circle(0, 0, 8 + 40 * t).stroke({ color: 0xffffff, width: 3, alpha: 1 - t });
    });
    ripple.destroy();
  }

  /** A short line rising from a point (a secret's prize). */
  private say(at: { x: number; y: number }, text: string): void {
    const t = new Text({ text, style: displayStyle(22) });
    t.anchor.set(0.5);
    t.position.set(at.x, at.y - 20);
    this.overlay.addChild(t);
    const y0 = t.y;
    void animate(1600, (k) => {
      t.y = y0 - 40 * ease.outCubic(k);
      t.alpha = k < 0.7 ? 1 : 1 - (k - 0.7) / 0.3;
    }).then(() => t.destroy());
  }

  /** Coins fly from `from` (canvas pixels) into the coin counter. */
  private async flyCoins(from: { x: number; y: number }, amount: number, iconAlias?: string): Promise<void> {
    if (amount <= 0) return;
    const target = this.opts.coinTarget?.() ?? { x: this.viewW - 80, y: 24 };
    this.host.stage.addChild(this.overlay);
    AudioBus.shared.play(REALM_SOUNDS.coins, { volume: 0.8 });
    await this.coinFlight.takeWin(amount, from, target, () => undefined, {
      totalDurationMs: 1100,
      flyDurationMs: 600,
      maxCoins: iconAlias ? undefined : Math.min(24, 8 + Math.round(Math.log2(1 + amount))),
      iconAlias,
      iconCount: iconAlias ? Math.min(10, Math.max(1, amount)) : undefined,
    });
  }
}
